import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import {
  VALID_PROPAGATE_LEVELS,
  type PropagateLevel,
} from "@/data/alarmRules/engineConstants";

// Wire enum values (the <strong> token) stay raw; only the help suffix is
// translated. Each entry's dict value is the FULL "Label — help" line; the
// component renders the part after " — " (matching the prior literal shape).
const PROPAGATE_HELP_KEY: Record<PropagateLevel, string> = {
  TENANT: "manage.rules.behaviour.propTenant",
  OWNER: "manage.rules.behaviour.propOwner",
  OWNER_HIERARCHY: "manage.rules.behaviour.propOwnerHierarchy",
  NONE: "manage.rules.behaviour.propNone",
};

interface Props {
  autoClear: boolean;
  onAutoClearChange: (next: boolean) => void;
  propagate: PropagateLevel;
  onPropagateChange: (next: PropagateLevel) => void;
}

/**
 * v4 — Behaviour panel.
 *
 * Two operator-visible controls share this section because they both
 * describe how the alarm behaves AFTER it fires (versus the per-type
 * config above, which describes WHEN it fires):
 *
 *  - **Auto-clear** (default ON): when the underlying condition resolves
 *    the alarm clears automatically. Operators disable for "sticky"
 *    alarms that require an explicit operator force-clear.
 *  - **Propagate** (default TENANT): how far the alarm spreads through
 *    ThingsBoard's ownership tree.
 *
 * The companion notify_on_clear toggle lives in the Recipients panel
 * because it only makes sense when there are recipients to email.
 */
export function BehaviourPanel({
  autoClear,
  onAutoClearChange,
  propagate,
  onPropagateChange,
}: Props) {
  const t = useT();
  return (
    <div className="border border-edge rounded p-3 space-y-3 bg-surface-2/30">
      <div className="text-[11px] uppercase tracking-wider text-text-faint">
        {t("manage.rules.behaviour.title")}
      </div>
      <label className="flex items-start gap-2 text-[12px] cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={autoClear}
          onChange={(e) => onAutoClearChange(e.target.checked)}
        />
        <span>
          <strong>{t("manage.rules.behaviour.autoClearTitle")}</strong>
          <span className="text-text-faint">
            &nbsp;{t("manage.rules.behaviour.autoClearHint")}
          </span>
        </span>
      </label>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-text-faint mb-1">
          {t("manage.rules.behaviour.propagation")}
        </div>
        <div className="space-y-1.5">
          {VALID_PROPAGATE_LEVELS.map((lvl) => (
            <label
              key={lvl}
              className={cn(
                "flex items-start gap-2 text-[12px] cursor-pointer",
                propagate === lvl && "text-text",
              )}
            >
              <input
                type="radio"
                name="propagate"
                className="mt-0.5"
                checked={propagate === lvl}
                onChange={() => onPropagateChange(lvl)}
              />
              <span>
                {/* Wire enum value stays raw; only the help suffix is translated. */}
                <strong>{lvl}</strong>
                <span className="text-text-faint">
                  &nbsp;— {t(PROPAGATE_HELP_KEY[lvl]).split(" — ")[1]}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
